import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ContactFormModal - Create/Edit contact form modal
 * Uses gold theme from BaseModal system
 * Shows supplier/customer-specific fields based on designation
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTruck, FaUser } from 'react-icons/fa';
import BaseModal, { Input as BaseInput, Textarea as BaseTextarea, FormGroup as BaseFormGroup, FormLabel, PrimaryButton, SecondaryButton, SectionTitle, } from './shared/BaseModal';
import { apiClient } from '../utils/apiClient';
import { notify } from '../utils/notifications';
import mockDataService from '../services/mockDataService';
import { validateContact, CONTACT_DESIGNATION_LABELS } from '../types/contact';
import { SUPPLIER_TYPE_LABELS } from '../types/supplier';
const FormGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const FormGroup = styled(BaseFormGroup) `
  grid-column: ${props => props.fullWidth ? 'span 2' : 'auto'};
  margin-bottom: 0;

  @media (max-width: 768px) {
    grid-column: auto;
  }
`;
const Input = styled(BaseInput) `
  padding: 12px 14px;
`;
const TextArea = styled(BaseTextarea) `
  min-height: 80px;
`;
const Select = styled.select `
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 3px var(--accent-gold-bg);
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;
const FormSectionTitle = styled(SectionTitle) `
  grid-column: span 2;
  margin-top: 24px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-column: auto;
  }

  &:first-child {
    margin-top: 0;
  }
`;
const ErrorText = styled.span `
  font-size: 12px;
  color: #DC2626;
  margin-top: 4px;
`;
const DesignationBanner = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 8px;
  background: ${props => {
    switch (props.designation) {
        case 'supplier': return 'rgba(184, 134, 11, 0.1)';
        case 'customer': return 'rgba(22, 163, 74, 0.1)';
        case 'both': return 'rgba(139, 92, 246, 0.1)';
        default: return 'var(--bg-tertiary)';
    }
}};
  border: 1px solid ${props => {
    switch (props.designation) {
        case 'supplier': return 'var(--accent-gold)';
        case 'customer': return '#22c55e';
        case 'both': return '#a78bfa';
        default: return 'var(--border-color)';
    }
}};
`;
const DesignationIcon = styled.div `
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.designation) {
        case 'supplier': return 'var(--accent-gold)';
        case 'customer': return '#22c55e';
        case 'both': return '#a78bfa';
        default: return 'var(--bg-secondary)';
    }
}};
  color: white;
  font-size: 16px;
`;
const DesignationInfo = styled.div `
  flex: 1;

  .title {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 14px;
  }

  .subtitle {
    font-size: 12px;
    color: var(--text-secondary);
    margin-top: 2px;
  }
`;
const SupplierSection = styled.div `
  background: rgba(184, 134, 11, 0.05);
  border: 1px solid rgba(184, 134, 11, 0.2);
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
`;
const CustomerSection = styled.div `
  background: rgba(22, 163, 74, 0.05);
  border: 1px solid rgba(22, 163, 74, 0.2);
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
`;
const SectionHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: var(--accent-gold);
  font-weight: 600;
  font-size: 14px;

  svg {
    font-size: 16px;
  }
`;
const FieldRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const ContactFormModal = ({ contact, onClose }) => {
    const isEditing = !!contact;
    const [loading, setLoading] = useState(false);
    const [loadingSupplier, setLoadingSupplier] = useState(false);
    const [errors, setErrors] = useState({});
    const [supplierData, setSupplierData] = useState(null);
    // Check if contact is a supplier or customer
    const isSupplier = contact?.designation === 'supplier' || contact?.designation === 'both';
    const isCustomer = contact?.designation === 'customer' || contact?.designation === 'both';
    const [form, setForm] = useState({
        // Basic contact fields
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        mobilePhone: '',
        fax: '',
        website: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        notes: '',
        tags: '',
        status: 'active',
        // Supplier-specific fields
        supplierCode: '',
        supplierType: 'wholesaler',
        paymentTerms: 'Net 30',
        taxId: '',
    });
    // Load contact data
    useEffect(() => {
        if (contact) {
            setForm(prev => ({
                ...prev,
                companyName: contact.companyName,
                contactPerson: contact.contactPerson,
                email: contact.email,
                phone: contact.phone,
                mobilePhone: contact.mobilePhone || '',
                fax: contact.fax || '',
                website: contact.website || '',
                street: contact.address?.street || '',
                city: contact.address?.city || '',
                state: contact.address?.state || '',
                zipCode: contact.address?.zipCode || '',
                country: contact.address?.country || '',
                notes: contact.notes || '',
                tags: contact.tags?.join(', ') || '',
                status: contact.status
            }));
            // Load supplier data if contact is a supplier
            if (contact.supplierId && (contact.designation === 'supplier' || contact.designation === 'both')) {
                loadSupplierData(contact.supplierId);
            }
        }
    }, [contact]);
    // Load supplier data from API or mock
    const loadSupplierData = async (supplierId) => {
        setLoadingSupplier(true);
        try {
            if (mockDataService.isMockMode()) {
                const supplier = mockDataService.getSupplier(supplierId);
                if (supplier) {
                    setSupplierData(supplier);
                    setForm(prev => ({
                        ...prev,
                        supplierCode: supplier.supplierCode || '',
                        supplierType: supplier.supplierType || 'wholesaler',
                        paymentTerms: supplier.paymentTerms || 'Net 30',
                        taxId: supplier.taxId || '',
                    }));
                }
            }
            else {
                const response = await apiClient.get(`suppliers/${supplierId}`);
                if (response.ok && response.data?.supplier) {
                    const supplier = response.data.supplier;
                    setSupplierData(supplier);
                    setForm(prev => ({
                        ...prev,
                        supplierCode: supplier.supplierCode || '',
                        supplierType: supplier.supplierType || 'wholesaler',
                        paymentTerms: supplier.paymentTerms || 'Net 30',
                        taxId: supplier.taxId || '',
                    }));
                }
            }
        }
        catch (error) {
            console.error('Failed to load supplier data:', error);
        }
        finally {
            setLoadingSupplier(false);
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        // Clear error when field is modified
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };
    const handleSubmit = async () => {
        // Build contact object for validation
        const contactData = {
            companyName: form.companyName,
            contactPerson: form.contactPerson,
            email: form.email,
            phone: form.phone,
            mobilePhone: form.mobilePhone || undefined,
            fax: form.fax || undefined,
            website: form.website || undefined,
            address: (form.street || form.city || form.state || form.zipCode || form.country) ? {
                street: form.street,
                city: form.city,
                state: form.state,
                zipCode: form.zipCode,
                country: form.country
            } : undefined,
            notes: form.notes || undefined,
            tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
            status: form.status
        };
        // Validate
        const validation = validateContact(contactData);
        if (!validation.isValid) {
            const newErrors = {};
            validation.errors.forEach(err => {
                newErrors[err.field] = err.message;
            });
            setErrors(newErrors);
            return;
        }
        setLoading(true);
        try {
            // Check if mock mode is enabled
            if (mockDataService.isMockMode()) {
                if (isEditing && contact) {
                    // Update contact
                    mockDataService.updateContact(contact.id, contactData);
                    // Also update supplier data if contact is a supplier
                    if (isSupplier && contact.supplierId) {
                        mockDataService.updateSupplier(contact.supplierId, {
                            supplierCode: form.supplierCode,
                            supplierType: form.supplierType,
                            paymentTerms: form.paymentTerms,
                            taxId: form.taxId || undefined,
                            // Also sync basic contact info to supplier
                            companyName: form.companyName,
                            contactPerson: form.contactPerson,
                            email: form.email,
                            phone: form.phone,
                            mobilePhone: form.mobilePhone || undefined,
                            fax: form.fax || undefined,
                            website: form.website || undefined,
                            address: contactData.address,
                        });
                    }
                    notify.success('Success', 'Contact updated successfully');
                }
                else {
                    // For new contacts in mock mode, add required fields
                    const newContactData = {
                        ...contactData,
                        designation: 'none',
                        status: form.status,
                        createdBy: 'MOCK_USER',
                    };
                    mockDataService.createContact(newContactData);
                    notify.success('Success', 'Contact created successfully');
                }
                onClose(true);
                return;
            }
            // Real API call
            let response;
            if (isEditing && contact) {
                // Update contact
                response = await apiClient.put(`contacts-update/${contact.id}`, contactData);
                // Also update supplier data if contact is a supplier
                if (response.ok && isSupplier && contact.supplierId) {
                    await apiClient.put(`suppliers/${contact.supplierId}`, {
                        supplierCode: form.supplierCode,
                        supplierType: form.supplierType,
                        paymentTerms: form.paymentTerms,
                        taxId: form.taxId || undefined,
                        // Also sync basic contact info to supplier
                        companyName: form.companyName,
                        contactPerson: form.contactPerson,
                        email: form.email,
                        phone: form.phone,
                        mobilePhone: form.mobilePhone || undefined,
                        fax: form.fax || undefined,
                        website: form.website || undefined,
                        address: contactData.address,
                    });
                }
            }
            else {
                response = await apiClient.post('contacts-create', contactData);
            }
            if (response.ok) {
                notify.success('Success', isEditing ? 'Contact updated successfully' : 'Contact created successfully');
                onClose(true);
            }
            else {
                const errorData = response.data;
                notify.error('Error', errorData?.error || 'Failed to save contact');
            }
        }
        catch (error) {
            console.error('Failed to save contact:', error);
            notify.error('Error', 'Failed to save contact');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs(BaseModal, { isOpen: true, onClose: () => onClose(false), title: isEditing ? 'Edit Contact' : 'Add New Contact', titleAccent: "Contact", size: "lg", children: [isEditing && contact && contact.designation !== 'none' && (_jsxs(DesignationBanner, { designation: contact.designation, children: [_jsx(DesignationIcon, { designation: contact.designation, children: contact.designation === 'customer' ? _jsx(FaUser, {}) : _jsx(FaTruck, {}) }), _jsxs(DesignationInfo, { children: [_jsx("div", { className: "title", children: CONTACT_DESIGNATION_LABELS[contact.designation] }), _jsxs("div", { className: "subtitle", children: [contact.designation === 'supplier' && 'This contact is assigned as a supplier', contact.designation === 'customer' && 'This contact is assigned as a customer', contact.designation === 'both' && 'This contact is assigned as both supplier and customer'] })] })] })), _jsxs(FormGrid, { children: [_jsx(FormSectionTitle, { children: "Basic Information" }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Company Name *" }), _jsx(Input, { name: "companyName", value: form.companyName, onChange: handleChange, placeholder: "Enter company name" }), errors.companyName && _jsx(ErrorText, { children: errors.companyName })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Contact Person *" }), _jsx(Input, { name: "contactPerson", value: form.contactPerson, onChange: handleChange, placeholder: "Primary contact name" }), errors.contactPerson && _jsx(ErrorText, { children: errors.contactPerson })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Email *" }), _jsx(Input, { name: "email", type: "email", value: form.email, onChange: handleChange, placeholder: "email@example.com" }), errors.email && _jsx(ErrorText, { children: errors.email })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Phone *" }), _jsx(Input, { name: "phone", value: form.phone, onChange: handleChange, placeholder: "Primary phone number" }), errors.phone && _jsx(ErrorText, { children: errors.phone })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Mobile Phone" }), _jsx(Input, { name: "mobilePhone", value: form.mobilePhone, onChange: handleChange, placeholder: "Mobile phone" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Fax" }), _jsx(Input, { name: "fax", value: form.fax, onChange: handleChange, placeholder: "Fax number" })] }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(FormLabel, { children: "Website" }), _jsx(Input, { name: "website", value: form.website, onChange: handleChange, placeholder: "https://example.com" })] }), _jsx(FormSectionTitle, { children: "Address" }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(FormLabel, { children: "Street Address" }), _jsx(Input, { name: "street", value: form.street, onChange: handleChange, placeholder: "Street address" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "City" }), _jsx(Input, { name: "city", value: form.city, onChange: handleChange, placeholder: "City" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "State/Province" }), _jsx(Input, { name: "state", value: form.state, onChange: handleChange, placeholder: "State" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Zip/Postal Code" }), _jsx(Input, { name: "zipCode", value: form.zipCode, onChange: handleChange, placeholder: "Zip code" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Country" }), _jsx(Input, { name: "country", value: form.country, onChange: handleChange, placeholder: "Country" })] }), _jsx(FormSectionTitle, { children: "Additional Information" }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Status" }), _jsxs(Select, { name: "status", value: form.status, onChange: handleChange, children: [_jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Tags (comma-separated)" }), _jsx(Input, { name: "tags", value: form.tags, onChange: handleChange, placeholder: "VIP, Preferred, International" })] }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(FormLabel, { children: "Notes" }), _jsx(TextArea, { name: "notes", value: form.notes, onChange: handleChange, placeholder: "Additional notes about this contact..." })] }), isSupplier && (_jsxs(_Fragment, { children: [_jsxs(FormSectionTitle, { style: { color: 'var(--accent-gold)' }, children: [_jsx(FaTruck, { style: { marginRight: '8px' } }), " Supplier Information"] }), _jsxs(SupplierSection, { style: { gridColumn: 'span 2' }, children: [loadingSupplier ? (_jsx("div", { style: { textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }, children: "Loading supplier data..." })) : (_jsxs(FieldRow, { children: [_jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Supplier Code" }), _jsx(Input, { name: "supplierCode", value: form.supplierCode, onChange: handleChange, placeholder: "e.g., SUP001" })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Supplier Type" }), _jsx(Select, { name: "supplierType", value: form.supplierType, onChange: handleChange, children: Object.entries(SUPPLIER_TYPE_LABELS).map(([value, label]) => (_jsx("option", { value: value, children: label }, value))) })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Payment Terms" }), _jsxs(Select, { name: "paymentTerms", value: form.paymentTerms, onChange: handleChange, children: [_jsx("option", { value: "Net 30", children: "Net 30" }), _jsx("option", { value: "Net 60", children: "Net 60" }), _jsx("option", { value: "Net 90", children: "Net 90" }), _jsx("option", { value: "COD", children: "COD (Cash on Delivery)" }), _jsx("option", { value: "Due on Receipt", children: "Due on Receipt" })] })] }), _jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Tax ID / EIN" }), _jsx(Input, { name: "taxId", value: form.taxId, onChange: handleChange, placeholder: "Tax identification number" })] })] })), supplierData && (_jsxs("div", { style: {
                                            marginTop: '16px',
                                            paddingTop: '16px',
                                            borderTop: '1px solid rgba(184, 134, 11, 0.2)',
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                            gap: '16px',
                                            fontSize: '13px'
                                        }, children: [_jsxs("div", { children: [_jsx("div", { style: { color: 'var(--text-secondary)', marginBottom: '4px' }, children: "Total Purchases" }), _jsxs("div", { style: { color: 'var(--accent-gold)', fontWeight: 600 }, children: ["$", supplierData.totalPurchases?.toLocaleString() || '0'] })] }), _jsxs("div", { children: [_jsx("div", { style: { color: 'var(--text-secondary)', marginBottom: '4px' }, children: "Amount Owed" }), _jsxs("div", { style: { color: supplierData.totalOwed > 0 ? '#ef4444' : 'var(--text-primary)', fontWeight: 600 }, children: ["$", supplierData.totalOwed?.toLocaleString() || '0'] })] }), _jsxs("div", { children: [_jsx("div", { style: { color: 'var(--text-secondary)', marginBottom: '4px' }, children: "Memo Debt" }), _jsxs("div", { style: { color: supplierData.totalMemoDebt > 0 ? '#f59e0b' : 'var(--text-primary)', fontWeight: 600 }, children: ["$", supplierData.totalMemoDebt?.toLocaleString() || '0'] })] })] }))] })] })), isCustomer && !isSupplier && (_jsxs(_Fragment, { children: [_jsxs(FormSectionTitle, { style: { color: '#22c55e' }, children: [_jsx(FaUser, { style: { marginRight: '8px' } }), " Customer Information"] }), _jsx(CustomerSection, { style: { gridColumn: 'span 2' }, children: _jsx("div", { style: { color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'center', padding: '12px' }, children: "Customer-specific fields are managed through the customer's account settings." }) })] }))] }), _jsxs(ButtonGroup, { children: [_jsx(SecondaryButton, { onClick: () => onClose(false), disabled: loading, children: "Cancel" }), _jsx(PrimaryButton, { onClick: handleSubmit, disabled: loading, children: loading ? 'Saving...' : (isEditing ? 'Update Contact' : 'Create Contact') })] })] }));
};
export default ContactFormModal;
